import type {
  PropertyGroup,
  RecentPropertySelection,
} from '@/lib/property-select/property-picker-model';
import type { UserPropertiesFeatureScope } from '@/lib/property-select/user-properties-scope';

export type TenantScopedSavedGroups = Record<
  UserPropertiesFeatureScope,
  PropertyGroup[]
>;
export type TenantScopedRecents = Record<
  UserPropertiesFeatureScope,
  RecentPropertySelection[]
>;
export type SavedGroupsStorageV2 = Record<string, TenantScopedSavedGroups>;
export type RecentsStorageV2 = Record<string, TenantScopedRecents>;

function emptyTenantScopedSavedGroups(): TenantScopedSavedGroups {
  return { global: [], lease_audits: [], due_diligence: [] };
}

function emptyTenantScopedRecents(): TenantScopedRecents {
  return { global: [], lease_audits: [], due_diligence: [] };
}

const V1_GROUPS_KEY = 'surface_user_property_groups';
const V1_RECENTS_KEY = 'surface_recent_property_selections';
const V2_GROUPS_KEY = 'surface_user_property_groups_v2';
const V2_RECENTS_KEY = 'surface_recent_property_selections_v2';
const MIGRATION_FLAG = 'surface_property_picker_v2_migrated';
const RECENTS_FEATURE_BUCKETS_RESET_FLAG =
  'surface_property_picker_recents_feature_buckets_reset_v1';

type LegacyTenantGroups = {
  managed: PropertyGroup[];
  severed: PropertyGroup[];
};
type LegacyTenantRecents = {
  managed: RecentPropertySelection[];
  severed: RecentPropertySelection[];
};

type LegacyGroupsRoot = Record<string, LegacyTenantGroups>;
type LegacyRecentsRoot = Record<string, LegacyTenantRecents>;

function parseJson<T>(raw: string | null): T | undefined {
  if (raw === null) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function mergeTenantIds(a: LegacyGroupsRoot, b: LegacyRecentsRoot): string[] {
  return Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
}

/**
 * One-time migration from managed/severed localStorage to feature-scoped v2 keys.
 * - Saved groups: `managed` → `global` union + `lease_audits`; `severed` → `due_diligence`
 * - Recents: **global only** (managed ∪ severed). `lease_audits` / `due_diligence` start empty
 *   so feature pickers do not inherit old tab-specific history.
 */
export function migratePropertyPickerLocalStorageV1ToV2(): void {
  if (typeof localStorage === 'undefined') return;
  if (localStorage.getItem(MIGRATION_FLAG) === '1') return;
  if (
    localStorage.getItem(V2_GROUPS_KEY) !== null ||
    localStorage.getItem(V2_RECENTS_KEY) !== null
  ) {
    localStorage.setItem(MIGRATION_FLAG, '1');
    return;
  }

  const v1Groups = parseJson<LegacyGroupsRoot>(
    localStorage.getItem(V1_GROUPS_KEY)
  );
  const v1Recents = parseJson<LegacyRecentsRoot>(
    localStorage.getItem(V1_RECENTS_KEY)
  );

  if (!v1Groups && !v1Recents) {
    localStorage.setItem(MIGRATION_FLAG, '1');
    return;
  }

  const outGroups: SavedGroupsStorageV2 = {};
  const outRecents: RecentsStorageV2 = {};
  const g = v1Groups ?? {};
  const r = v1Recents ?? {};

  for (const tenantId of mergeTenantIds(g, r)) {
    const lg = g[tenantId] ?? { managed: [], severed: [] };
    const lr = r[tenantId] ?? { managed: [], severed: [] };

    const tg: TenantScopedSavedGroups = emptyTenantScopedSavedGroups();
    tg.global = [...lg.managed, ...lg.severed];
    tg.lease_audits = [...lg.managed];
    tg.due_diligence = [...lg.severed];
    outGroups[tenantId] = tg;

    const tr: TenantScopedRecents = emptyTenantScopedRecents();
    tr.global = [...lr.managed, ...lr.severed];
    outRecents[tenantId] = tr;
  }

  localStorage.setItem(V2_GROUPS_KEY, JSON.stringify(outGroups));
  localStorage.setItem(V2_RECENTS_KEY, JSON.stringify(outRecents));
  localStorage.removeItem(V1_GROUPS_KEY);
  localStorage.removeItem(V1_RECENTS_KEY);
  localStorage.setItem(MIGRATION_FLAG, '1');
}

/**
 * One-time cleanup for browsers that already ran v1→v2 when recents were copied
 * into `lease_audits` / `due_diligence`. Clears those buckets; leaves `global` intact.
 */
export function clearFeatureRecentsBucketsOnce(): void {
  if (typeof localStorage === 'undefined') return;
  if (localStorage.getItem(RECENTS_FEATURE_BUCKETS_RESET_FLAG) === '1') {
    return;
  }

  const raw = localStorage.getItem(V2_RECENTS_KEY);
  if (!raw) {
    localStorage.setItem(RECENTS_FEATURE_BUCKETS_RESET_FLAG, '1');
    return;
  }

  try {
    const data = parseJson<RecentsStorageV2>(raw);
    if (!data) {
      localStorage.setItem(RECENTS_FEATURE_BUCKETS_RESET_FLAG, '1');
      return;
    }

    let changed = false;
    for (const tenantId of Object.keys(data)) {
      const row = data[tenantId];
      if (!row || typeof row !== 'object') continue;
      if (
        (row.lease_audits?.length ?? 0) > 0 ||
        (row.due_diligence?.length ?? 0) > 0
      ) {
        row.lease_audits = [];
        row.due_diligence = [];
        changed = true;
      }
    }

    if (changed) {
      localStorage.setItem(V2_RECENTS_KEY, JSON.stringify(data));
    }
  } catch {
    /* leave storage as-is */
  }

  localStorage.setItem(RECENTS_FEATURE_BUCKETS_RESET_FLAG, '1');
}

export const PROPERTY_PICKER_V2_GROUPS_LS_KEY = V2_GROUPS_KEY;
export const PROPERTY_PICKER_V2_RECENTS_LS_KEY = V2_RECENTS_KEY;
